/* global Word */
import * as React from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import {
  Button,
  Input,
  Text,
  Badge,
  Spinner,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  MicRegular,
  MicOffRegular,
  SendRegular,
  PlayRegular,
  StopRegular,
  ChatRegular,
} from "@fluentui/react-icons";
import {
  useConversationControls,
  useConversationStatus,
  useConversationMode,
  useConversationInput,
  useConversationClientTool,
} from "@elevenlabs/react";
import { ChatMessage, ChatMessageData } from "./ChatMessage";
import * as wordTools from "../wordTools";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "400px",
  },
  statusBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  statusLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  messageList: {
    flex: 1,
    overflowY: "auto",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  emptyState: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    textAlign: "center",
  },
  inputBar: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  textInput: {
    flex: 1,
  },
  controlsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "8px 12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
});

interface ChatPanelProps {
  agentId: string;
  checkRevisions?: () => void;
}

let messageIdCounter = 0;

export const ChatPanel: React.FC<ChatPanelProps> = ({ agentId, checkRevisions }) => {
  const styles = useStyles();
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [inputText, setInputText] = useState("");
  const messageListRef = useRef<HTMLDivElement>(null);

  const { startSession, endSession, sendUserMessage, sendUserActivity } = useConversationControls();
  const { status } = useConversationStatus();
  const { isSpeaking } = useConversationMode();
  const { isMuted, setMuted } = useConversationInput();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const el = messageListRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Helper: wrap a tool call with error handling and revision check
  const wrapTool = useCallback(
    async (fn: () => Promise<string>): Promise<string> => {
      try {
        const result = await fn();
        return result;
      } catch (error) {
        console.error("[wordTool] Error:", error);
        return `Errore: ${error instanceof Error ? error.message : String(error)}`;
      } finally {
        checkRevisions?.();
      }
    },
    [checkRevisions]
  );

  // --- Register all document tools ---

  useConversationClientTool("get_document_text", async () => {
    return wrapTool(() => wordTools.getDocumentText());
  });

  useConversationClientTool("add_paragraph", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.addParagraph(
      String(args.text || ""),
      args.style ? String(args.style) : undefined
    ));
  });

  useConversationClientTool("add_heading", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.addHeading(
      String(args.text || ""),
      Number(args.level) || 1
    ));
  });

  useConversationClientTool("delete_paragraph", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.deleteParagraph(Number(args.paragraph_index)));
  });

  useConversationClientTool("delete_sentence", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.deleteSentence(String(args.sentence || "")));
  });

  useConversationClientTool("replace_text", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.replaceText(
      String(args.find_text || ""),
      String(args.replace_text || "")
    ));
  });

  useConversationClientTool("replace_text_in_document", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.replaceText(
      String(args.param_old_text || args.find_text || ""),
      String(args.param_new_text || args.replace_text || "")
    ));
  });

  useConversationClientTool("format_paragraph", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.formatParagraph(Number(args.paragraph_index), {
      bold: args.bold as boolean | undefined,
      italic: args.italic as boolean | undefined,
      underline: args.underline as boolean | undefined,
      font_size: args.font_size ? Number(args.font_size) : undefined,
      color: args.color ? String(args.color) : undefined,
    }));
  });

  useConversationClientTool("add_table", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.addTable(
      Number(args.rows) || 1,
      Number(args.cols) || 1,
      args.data as string[][] | undefined
    ));
  });

  useConversationClientTool("insert_paragraph_after", async (args: Record<string, unknown>) => {
    return wrapTool(() => wordTools.insertParagraphAfter(
      String(args.target_text || ""),
      String(args.new_text || "")
    ));
  });

  useConversationClientTool("get_word_count", async () => {
    return wrapTool(() => wordTools.getWordCount());
  });

  useConversationClientTool("add_page_break", async () => {
    return wrapTool(() => wordTools.addPageBreak());
  });

  // --- Message handling ---
  const addMessage = useCallback((role: "user" | "agent", text: string, isFinal: boolean) => {
    const id = `msg-${++messageIdCounter}`;
    setMessages((prev) => {
      // For agent streaming: update the last agent message if it's not final
      if (role === "agent" && prev.length > 0) {
        const last = prev[prev.length - 1];
        if (last.role === "agent" && !last.isFinal) {
          return [...prev.slice(0, -1), { ...last, text, isFinal }];
        }
      }
      return [...prev, { id, role, text, timestamp: new Date(), isFinal }];
    });
  }, []);

  // --- Session controls ---
  const handleStartSession = useCallback(async () => {
    try {
      await startSession({
        agentId,
        onMessage: (message) => {
          // Ignore empty transcriptions (silence / background noise)
          if (!message.message || !message.message.trim()) return;
          if (message.role === "agent") {
            addMessage("agent", message.message, true);
          } else if (message.role === "user") {
            addMessage("user", message.message, true);
          }
        },
        onError: (error) => {
          console.error("ElevenLabs conversation error:", error);
        },
        onDisconnect: () => {
          console.log("ElevenLabs conversation disconnected");
        },
      });
    } catch (error) {
      console.error("Failed to start session:", error);
    }
  }, [agentId, startSession, addMessage]);

  const handleEndSession = useCallback(async () => {
    try {
      await endSession();
    } catch (error) {
      console.error("Failed to end session:", error);
    }
  }, [endSession]);

  const handleSendText = useCallback(() => {
    const text = inputText.trim();
    if (!text || status !== "connected") return;
    addMessage("user", text, true);
    sendUserMessage(text);
    setInputText("");
  }, [inputText, status, sendUserMessage, addMessage]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputText(e.target.value);
      if (status === "connected") {
        sendUserActivity();
      }
    },
    [status, sendUserActivity]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendText();
      }
    },
    [handleSendText]
  );

  // --- Status badge ---
  const statusColor = status === "connected" ? "success" : status === "connecting" ? "warning" : "informative";
  const statusLabel =
    status === "connected"
      ? isSpeaking
        ? "Agente sta parlando..."
        : "Connesso"
      : status === "connecting"
        ? "Connessione..."
        : "Disconnesso";

  return (
    <div className={styles.container}>
      {/* Status bar */}
      <div className={styles.statusBar}>
        <div className={styles.statusLeft}>
          <Badge appearance="filled" color={statusColor} />
          <Text size={200}>{statusLabel}</Text>
        </div>
        {status === "connected" && (
          <Tooltip content={isMuted ? "Attiva microfono" : "Disattiva microfono"} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={isMuted ? <MicOffRegular /> : <MicRegular />}
              onClick={() => setMuted(!isMuted)}
            />
          </Tooltip>
        )}
      </div>

      {/* Message list */}
      {messages.length === 0 ? (
        <div className={styles.emptyState}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground4 }}>
            {status === "connected" ? (
              <>
                <ChatRegular style={{ fontSize: "32px", display: "block", margin: "0 auto 8px" }} />
                Inizia a parlare o scrivi un messaggio
              </>
            ) : (
              "Avvia la sessione per conversare con l'agente"
            )}
          </Text>
        </div>
      ) : (
        <div className={styles.messageList} ref={messageListRef}>
          {messages.map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
        </div>
      )}

      {/* Text input bar */}
      {status === "connected" && (
        <div className={styles.inputBar}>
          <Input
            className={styles.textInput}
            placeholder="Scrivi un messaggio..."
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            size="small"
          />
          <Tooltip content="Invia messaggio" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<SendRegular />}
              onClick={handleSendText}
              disabled={!inputText.trim()}
            />
          </Tooltip>
        </div>
      )}

      {/* Session controls */}
      <div className={styles.controlsBar}>
        {status === "disconnected" && (
          <Button appearance="primary" icon={<PlayRegular />} onClick={handleStartSession}>
            Avvia Conversazione
          </Button>
        )}
        {status === "connecting" && <Spinner size="small" label="Connessione in corso..." />}
        {status === "connected" && (
          <Button appearance="secondary" icon={<StopRegular />} onClick={handleEndSession}>
            Termina Conversazione
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChatPanel;
